'use client'

import { IconArrowLeft, IconArrowRight, IconExternalLink, IconPlayerPause, IconPlayerPlay, IconX } from '@tabler/icons-react'
import { motion, AnimatePresence } from 'framer-motion'
import Image from 'next/image'
import { useEffect, useState, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'

type Testimonial = {
  quote: string
  name: string
  designation: string
  src?: string
  initials?: string
  url?: string
}

// Lista de screenshots full que existem (21 de 21)
const existingFullScreenshots = [
  'desafios-do-pai',
  'diarista-vip',
  'eliore-studio',
  'grupo-costa-leste',
  'harmonizacao-facial',
  'harmonizacao-glutea',
  'homepar',
  'instituto-med-menosso',
  'liboni-bauer',
  'lite-ti',
  'litoral-comunicacao',
  'marques-gas-agua',
  'mondelle-brasil',
  'natan-cardeal',
  'orestes-cabeleireiro',
  'ponto-do-carro',
  'prospec-engenharia',
  'sos-diarista',
  'terragreen',
  'tupa-fc',
  'ultragas-itajai',
]

// Detect mobile device
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const check = () => {
      setIsMobile(window.innerWidth < 768 ||
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent))
    }
    check()
    window.addEventListener('resize', check, { passive: true })
    return () => window.removeEventListener('resize', check)
  }, [])
  return isMobile
}

function ProjectImage({ name, src, initials, isActive, isPaused, isMobile, onExpand }: { name: string; src?: string; initials?: string; isActive?: boolean; isPaused?: boolean; isMobile?: boolean; onExpand?: () => void }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [scrollPercent, setScrollPercent] = useState(0)

  // Calcular a porcentagem de scroll baseada nas dimensões reais
  const handleImageLoad = useCallback((img: HTMLImageElement) => {
    if (containerRef.current && img) {
      const containerHeight = containerRef.current.offsetHeight
      const imageHeight = img.naturalHeight * (containerRef.current.offsetWidth / img.naturalWidth)

      if (imageHeight > containerHeight) {
        // Quanto precisa mover para mostrar o final da imagem (99% do calculado)
        const scrollAmount = ((imageHeight - containerHeight) / imageHeight) * 100 * 0.999
        setScrollPercent(scrollAmount)
      }
    }
  }, [])

  // Gerar o caminho do screenshot full page baseado no src
  const getFullScreenshotSrc = (originalSrc: string) => {
    const filename = originalSrc.split('/').pop()?.replace('.webp', '') || ''
    // Verificar se o screenshot full existe
    if (!existingFullScreenshots.includes(filename)) {
      return null
    }
    return `/portfolio/full/${filename}.webp`
  }

  if (src) {
    const fullSrc = getFullScreenshotSrc(src)
    const hasFullScreenshot = fullSrc !== null
    // Mobile: usar full-mobile (640x~2000, ~45KB) — leve e alto o suficiente para scroll
    // Desktop: usar full screenshot (1280x~10000, ~180KB) para animação de scroll
    const imageSrc = hasFullScreenshot && fullSrc
      ? (isMobile ? fullSrc.replace('/full/', '/full-mobile/') : fullSrc)
      : src
    const shouldAnimate = hasFullScreenshot && isActive && scrollPercent > 0

    return (
      <div
        ref={containerRef}
        className="portfolio-image-container relative w-full h-full rounded-3xl overflow-hidden bg-gray-100 dark:bg-gray-900 cursor-pointer"
        onClick={() => { if (isActive && hasFullScreenshot) onExpand?.() }}
      >
        <Image
          src={imageSrc}
          alt={name}
          width={isMobile ? 640 : 1280}
          height={isMobile ? 2000 : 4000}
          sizes="(max-width: 768px) 100vw, 60vw"
          className={cn(
            "w-full h-auto rounded-3xl",
            shouldAnimate ? "animate-scroll-dynamic" : "",
            shouldAnimate && isMobile ? "animate-scroll-mobile-delay" : "",
            isPaused ? "animation-paused" : ""
          )}
          style={{
            '--scroll-percent': `-${scrollPercent}%`
          } as React.CSSProperties}
          onLoad={(e) => handleImageLoad(e.currentTarget)}
          priority={isActive}
          loading={isActive ? undefined : 'lazy'}
        />
      </div>
    )
  }

  const displayInitials = initials || name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

  return (
    <div className="h-full w-full rounded-3xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-white font-bold text-7xl">
      {displayInitials}
    </div>
  )
}

function usePinchZoom() {
  const containerRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const stateRef = useRef({ scale: 1, x: 0, y: 0, startDist: 0, startScale: 1, startX: 0, startY: 0, panStartX: 0, panStartY: 0, isPinching: false, lastTap: 0 })

  useEffect(() => {
    const container = containerRef.current
    const img = imgRef.current
    if (!container || !img) return

    const s = stateRef.current
    const updateTouchAction = () => {
      img.style.touchAction = s.scale > 1 ? 'none' : 'pan-y'
    }
    const applyTransform = () => {
      img.style.transform = `translate(${s.x}px, ${s.y}px) scale(${s.scale})`
    }
    const resetZoom = () => {
      s.scale = 1; s.x = 0; s.y = 0
      img.style.transition = 'transform 0.25s ease'
      applyTransform()
      updateTouchAction()
      setTimeout(() => { img.style.transition = '' }, 250)
    }
    const clampPan = () => {
      if (s.scale <= 1) { s.x = 0; s.y = 0; return }
      const rect = container.getBoundingClientRect()
      const imgW = img.naturalWidth / img.naturalHeight * rect.height
      const maxX = Math.max(0, (imgW * s.scale - rect.width) / 2)
      const maxY = Math.max(0, (rect.height * s.scale - rect.height) / 2)
      s.x = Math.max(-maxX, Math.min(maxX, s.x))
      s.y = Math.max(-maxY, Math.min(maxY, s.y))
    }
    const getDist = (t: TouchList) => Math.hypot(t[1].clientX - t[0].clientX, t[1].clientY - t[0].clientY)
    const getMid = (t: TouchList) => ({ x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 })

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault()
        s.isPinching = true
        s.startDist = getDist(e.touches)
        s.startScale = s.scale
        const mid = getMid(e.touches)
        s.panStartX = mid.x - s.x
        s.panStartY = mid.y - s.y
      } else if (e.touches.length === 1 && s.scale > 1) {
        s.panStartX = e.touches[0].clientX - s.x
        s.panStartY = e.touches[0].clientY - s.y
      }
      // Double-tap detection
      if (e.touches.length === 1) {
        const now = Date.now()
        if (now - s.lastTap < 300) {
          e.preventDefault()
          if (s.scale > 1) { resetZoom() } else {
            s.scale = 2.5
            const rect = container.getBoundingClientRect()
            s.x = (rect.width / 2 - e.touches[0].clientX) * 1.5
            s.y = (rect.height / 2 - e.touches[0].clientY) * 1.5
            clampPan()
            img.style.transition = 'transform 0.25s ease'
            applyTransform()
            setTimeout(() => { img.style.transition = '' }, 250)
          }
        }
        s.lastTap = now
      }
    }
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && s.isPinching) {
        e.preventDefault()
        const dist = getDist(e.touches)
        s.scale = Math.max(1, Math.min(5, s.startScale * (dist / s.startDist)))
        const mid = getMid(e.touches)
        s.x = mid.x - s.panStartX
        s.y = mid.y - s.panStartY
        clampPan()
        applyTransform()
        updateTouchAction()
      } else if (e.touches.length === 1 && s.scale > 1) {
        e.preventDefault()
        s.x = e.touches[0].clientX - s.panStartX
        s.y = e.touches[0].clientY - s.panStartY
        clampPan()
        applyTransform()
      }
    }
    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) s.isPinching = false
      if (s.scale <= 1.05) resetZoom()
    }

    container.addEventListener('touchstart', onTouchStart, { passive: false })
    container.addEventListener('touchmove', onTouchMove, { passive: false })
    container.addEventListener('touchend', onTouchEnd)
    return () => {
      container.removeEventListener('touchstart', onTouchStart)
      container.removeEventListener('touchmove', onTouchMove)
      container.removeEventListener('touchend', onTouchEnd)
    }
  }, [])

  return { containerRef, imgRef }
}

function PortfolioLightbox({ src, name, url, onClose }: { src: string; name: string; url?: string; onClose: () => void }) {
  const [isMobileLB, setIsMobileLB] = useState(false)
  const { containerRef: zoomContainerRef, imgRef: zoomImgRef } = usePinchZoom()

  useEffect(() => {
    setIsMobileLB(window.innerWidth < 768)
    document.body.style.overflow = 'hidden'
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handleKey)
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', handleKey)
    }
  }, [onClose])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[200] flex flex-col bg-black/90 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 shrink-0" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <span className="text-white font-semibold text-sm sm:text-base truncate max-w-[200px] sm:max-w-none">{name}</span>
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white/80 text-xs font-medium hover:bg-white/20 transition-colors"
            >
              Visitar site
              <IconExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
        <button
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          aria-label="Fechar"
        >
          <IconX className="w-5 h-5" />
        </button>
      </div>

      {/* Scrollable image with pinch-to-zoom */}
      <div
        ref={zoomContainerRef}
        className="flex-1 overflow-y-auto overflow-x-hidden px-4 sm:px-8 pb-6 scrollbar-portfolio touch-pan-y"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="max-w-4xl mx-auto">
          <Image
            ref={zoomImgRef}
            src={isMobileLB ? src.replace('/full/', '/full-mobile/') : src}
            alt={name}
            width={1280}
            height={8000}
            sizes="(max-width: 768px) 100vw, 896px"
            loading="eager"
            className="w-full h-auto rounded-2xl origin-center"
            style={{ touchAction: 'pan-y' }}
          />
        </div>
      </div>
    </motion.div>
  )
}

export const AnimatedTestimonials = ({
  testimonials,
  autoplay = false,
  className,
}: {
  testimonials: Testimonial[]
  autoplay?: boolean
  className?: string
}) => {
  const [active, setActive] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const [lightbox, setLightbox] = useState<{ src: string; name: string; url?: string } | null>(null)
  const isMobile = useIsMobile()

  // Pre-generate random rotations on mount to avoid hydration mismatch
  const rotationsRef = useRef<number[]>([])

  useEffect(() => {
    rotationsRef.current = testimonials.map(() => Math.floor(Math.random() * 21) - 10)
    setIsMounted(true)
  }, [testimonials.length])

  const handleNext = useCallback(() => {
    setActive((prev) => (prev + 1) % testimonials.length)
  }, [testimonials.length])

  const handlePrev = useCallback(() => {
    setActive((prev) => (prev - 1 + testimonials.length) % testimonials.length)
  }, [testimonials.length])

  const isActive = (index: number) => {
    return index === active
  }

  useEffect(() => {
    if (autoplay) {
      const interval = setInterval(handleNext, 5000)
      return () => clearInterval(interval)
    }
  }, [autoplay, handleNext])

  const getRotation = (index: number) => rotationsRef.current[index] || 0

  // Don't render carousel until mounted to avoid hydration mismatch
  if (!isMounted) {
    return (
      <div className={cn('max-w-md md:max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-6 sm:py-12', className)}>
        <div className="relative grid grid-cols-1 md:grid-cols-[1.5fr_1fr] gap-5 sm:gap-8 md:gap-20 items-center">
          <div className="relative aspect-[16/9] w-full" />
        </div>
      </div>
    )
  }

  return (
    <div className={cn('max-w-md md:max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-6 sm:py-12', className)}>
      <div className="relative grid grid-cols-1 md:grid-cols-[1.5fr_1fr] gap-5 sm:gap-8 md:gap-20 items-center">
        {/* Image Carousel - Aspect ratio 16:9 para sites */}
        <div className="relative aspect-[16/9] w-full">
          <AnimatePresence>
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{
                  opacity: 0,
                  scale: 0.9,
                  z: -100,
                  rotate: getRotation(index),
                }}
                animate={{
                  opacity: isActive(index) ? 1 : 0.7,
                  scale: isActive(index) ? 1 : 0.95,
                  z: isActive(index) ? 0 : -100,
                  rotate: isActive(index) ? 0 : getRotation(index),
                  zIndex: isActive(index)
                    ? 50
                    : testimonials.length + 2 - index,
                  y: isActive(index) ? [0, -30, 0] : 0,
                }}
                exit={{
                  opacity: 0,
                  scale: 0.9,
                  z: 100,
                  rotate: getRotation(index),
                }}
                transition={{
                  duration: 0.4,
                  ease: 'easeInOut',
                }}
                className="absolute inset-0 origin-bottom"
              >
                <ProjectImage
                  name={testimonial.name}
                  src={testimonial.src}
                  initials={testimonial.initials}
                  isActive={isActive(index)}
                  isPaused={isPaused}
                  isMobile={isMobile}
                  onExpand={() => {
                    if (testimonial.src) {
                      const filename = testimonial.src.split('/').pop()?.replace('.webp', '') || ''
                      const fullSrc = existingFullScreenshots.includes(filename)
                        ? `/portfolio/full/${filename}.webp`
                        : testimonial.src
                      setLightbox({ src: fullSrc, name: testimonial.name, url: testimonial.url })
                    }
                  }}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Content - Lado direito (centralizado no mobile, esquerda no desktop) */}
        <div className="flex flex-col justify-between h-full py-2 items-center md:items-start text-center md:text-left">
          <motion.div
            key={active}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
          >
            <h3 className="text-xl md:text-2xl font-bold text-indigo-950 dark:text-white">
              {testimonials[active].name}
            </h3>
            <p className="text-sm text-indigo-950/60 dark:text-white/60 mb-4">
              {testimonials[active].designation}
            </p>

            {/* Word-by-word animation - simplified on mobile for performance */}
            {isMobile ? (
              <motion.p
                className="text-base text-indigo-950/70 dark:text-white/70"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                {testimonials[active].quote}
              </motion.p>
            ) : (
              <motion.p className="text-base text-indigo-950/70 dark:text-white/70">
                {testimonials[active].quote.split(' ').map((word, index) => (
                  <motion.span
                    key={index}
                    initial={{
                      filter: 'blur(10px)',
                      opacity: 0,
                      y: 5,
                    }}
                    animate={{
                      filter: 'blur(0px)',
                      opacity: 1,
                      y: 0,
                    }}
                    transition={{
                      duration: 0.2,
                      ease: 'easeInOut',
                      delay: 0.02 * index,
                    }}
                    className="inline-block"
                  >
                    {word}&nbsp;
                  </motion.span>
                ))}
              </motion.p>
            )}

            {/* Botão para visitar o site */}
            {testimonials[active].url && (
              <motion.a
                href={testimonials[active].url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3, duration: 0.3 }}
                className={cn(
                  "inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-full bg-white/80 dark:bg-white/10 border border-purple-500/30 text-purple-600 dark:text-purple-400 text-sm font-medium hover:border-purple-500 hover:shadow-lg hover:shadow-purple-500/20 transition-all group",
                  !isMobile && "backdrop-blur-sm"
                )}
              >
                Visitar site
                <IconExternalLink className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </motion.a>
            )}
          </motion.div>

          {/* Navigation */}
          <div className="flex items-center justify-center md:justify-start gap-4 mt-6">
            <button
              onClick={handlePrev}
              className={cn(
                "h-10 w-10 rounded-full bg-white/80 dark:bg-neutral-800/80 border border-indigo-950/10 dark:border-white/10 flex items-center justify-center group/button hover:border-purple-500 transition-all duration-200 shadow-md",
                !isMobile && "backdrop-blur-sm"
              )}
              aria-label="Projeto anterior"
            >
              <IconArrowLeft className="h-5 w-5 text-indigo-950 dark:text-white group-hover/button:rotate-12 transition-transform duration-300" />
            </button>

            {/* Botão pause/play - visível apenas em mobile/tablet */}
            <button
              onClick={() => setIsPaused(!isPaused)}
              className="md:hidden h-10 w-10 rounded-full bg-white/80 dark:bg-neutral-800/80 border border-purple-500/30 dark:border-purple-400/30 flex items-center justify-center group/button hover:border-purple-500 transition-all duration-200 shadow-md"
              aria-label={isPaused ? "Retomar animação" : "Pausar animação"}
            >
              {isPaused ? (
                <IconPlayerPlay className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              ) : (
                <IconPlayerPause className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              )}
            </button>

            <button
              onClick={handleNext}
              className={cn(
                "h-10 w-10 rounded-full bg-white/80 dark:bg-neutral-800/80 border border-indigo-950/10 dark:border-white/10 flex items-center justify-center group/button hover:border-purple-500 transition-all duration-200 shadow-md",
                !isMobile && "backdrop-blur-sm"
              )}
              aria-label="Próximo projeto"
            >
              <IconArrowRight className="h-5 w-5 text-indigo-950 dark:text-white group-hover/button:-rotate-12 transition-transform duration-300" />
            </button>
          </div>
        </div>
      </div>

      {/* Portfolio Lightbox — portal to body to escape stacking contexts */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {lightbox && (
            <PortfolioLightbox
              src={lightbox.src}
              name={lightbox.name}
              url={lightbox.url}
              onClose={() => setLightbox(null)}
            />
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  )
}
